import { REGULATORY_WATCH_TABLE_PAGE_SIZE } from '@/components/table/constants';
import { ControlledSortColumnHeader } from '@/components/table/SortColumnHeader';
import { TablePaginationStickyFooter } from '@/components/table/TablePaginationStickyFooter';
import { Button } from '@/components/ui/button';
import { CountryFilter } from '@/components/ui/country-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import {
  FilterPopoverTrigger,
  type FilterPopoverTriggerVisualProps,
} from '@/components/ui/filter-popover-trigger';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/useDebounce';
import {
  useUrlParam,
  useUrlParamCsv,
  useUrlParamEnum,
} from '@/hooks/useUrlParam';
import {
  useAllDocumentsService,
  useSourcesService,
} from '@/services/regulatoryWatch';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import { TYPE_BADGE_CONFIG } from '@/shared/subscriptions/OptionFilter';
import { cn } from '@/utils/classUtils';
import { getSourceIconUrl } from '@/utils/source-icon';
import type {
  AllDocumentsCountryGroup,
  AllDocumentsReferenceItem,
  AllDocumentsSourceGroup,
  ListAllDocumentsQuery,
} from '@ditto/shared';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';
import {
  Check,
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ExternalLink,
  FileText,
  Folder,
  Plus,
  Search,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  DocumentTypeBadge,
  DocumentTypeFilterGlyph,
} from '../components/columns';
import { loadRwPageSession } from '../hooks/regulatoryWatchPageSession';
import {
  usePersistToRwSession,
  useRegulatoryWatchScrollSession,
  useRegulatoryWatchSearchSession,
} from '../hooks/useRegulatoryWatchPageSession';
import { SOURCE_TYPE_LABELS, SOURCE_TYPE_OPTIONS } from '../labels';
import { LastUpdatedCell } from './LastUpdatedCell';
import {
  LAST_UPDATED_PRESET_VALUES,
  LastUpdatedFilter,
  type LastUpdatedPreset,
} from './LastUpdatedFilter';
import { LastUpdatedPreviewProvider } from './LastUpdatedPreviewContext';

type GroupBy = 'source' | 'country' | 'type' | 'none';

type SortableColumn = 'name' | 'lastUpdated' | 'type' | 'region' | 'source';

type TableSortState = {
  column: SortableColumn;
  direction: 'asc' | 'desc';
} | null;

const SORTABLE_COLUMNS: readonly SortableColumn[] = [
  'name',
  'lastUpdated',
  'type',
  'region',
  'source',
];

function parsePersistedTableSort(
  raw: {
    column: string;
    direction: 'asc' | 'desc';
  } | null
): TableSortState {
  if (!raw) return null;
  if (!SORTABLE_COLUMNS.includes(raw.column as SortableColumn)) return null;
  return {
    column: raw.column as SortableColumn,
    direction: raw.direction,
  };
}

type ColumnWidths = {
  lastUpdated: number;
  type: number;
  region: number;
  source: number;
  action: number;
};

const DEFAULT_COLUMN_WIDTHS: ColumnWidths = {
  lastUpdated: 130,
  type: 160,
  region: 160,
  source: 160,
  action: 72,
};

const MY_REFERENCES_FILTER_TRIGGER: FilterPopoverTriggerVisualProps = {
  size: 'sm',
  activeAppearance: 'outline',
  operatorClassName: 'text-muted-foreground',
};

function renderReferenceTypeFilterSummary(selected: string[]): ReactNode {
  const n = selected.length;
  if (n === 0) return null;
  if (n === 1) {
    const t = selected[0]!;
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
        <DocumentTypeFilterGlyph type={t} />
        <span className="truncate">{SOURCE_TYPE_LABELS[t] ?? t}</span>
      </span>
    );
  }
  const iconCount = n > 3 ? 3 : n;
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      {selected.slice(0, iconCount).map(t => (
        <DocumentTypeFilterGlyph key={t} type={t} />
      ))}
      <span className="shrink-0">{n} selected</span>
    </span>
  );
}

function renderGeographyFilterSummary(codes: string[]): ReactNode {
  const n = codes.length;
  if (n === 0) return null;
  if (n === 1) {
    const code = codes[0]!;
    const flag = getGeographyFlag(code);
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
        {flag && (
          <img src={flag} alt="" className="size-4 shrink-0 object-contain" />
        )}
        <span className="truncate">{getGeographyLabel(code)}</span>
      </span>
    );
  }
  const show = n > 3 ? 3 : n;
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      {codes.slice(0, show).map(code => {
        const flag = getGeographyFlag(code);
        return flag ? (
          <img
            key={code}
            src={flag}
            alt=""
            className="size-4 shrink-0 object-contain"
          />
        ) : null;
      })}
      <span className="shrink-0">{n} selected</span>
    </span>
  );
}

function renderSourceFilterSummary(
  selected: string[],
  options: FilterOption[]
): ReactNode {
  const n = selected.length;
  if (n === 0) return null;
  if (n === 1) {
    const opt = options.find(o => o.value === selected[0]);
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
        {opt?.icon && (
          <img
            src={opt.icon}
            alt=""
            className="size-4 shrink-0 rounded-full object-contain border border-[#e5e5e5]"
          />
        )}
        <span className="truncate">{opt?.label ?? selected[0]}</span>
      </span>
    );
  }
  const show = n > 3 ? 3 : n;
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      {selected.slice(0, show).map(id => {
        const opt = options.find(o => o.value === id);
        return opt?.icon ? (
          <img
            key={id}
            src={opt.icon}
            alt=""
            className="size-4 shrink-0 rounded-full object-contain border border-[#e5e5e5]"
          />
        ) : (
          <span
            key={id}
            className="size-4 shrink-0 rounded-full bg-neutral-100"
            aria-hidden
          />
        );
      })}
      <span className="shrink-0">{n} selected</span>
    </span>
  );
}

// ── Sort helpers ──

function cycleTableSort(
  prev: TableSortState,
  column: SortableColumn
): TableSortState {
  if (!prev || prev.column !== column) {
    // Date-oriented: first click is descending (most recent / latest first).
    if (column === 'lastUpdated') {
      return { column, direction: 'desc' };
    }
    return { column, direction: 'asc' };
  }
  if (column === 'lastUpdated') {
    if (prev.direction === 'desc') return { column, direction: 'asc' };
    return null;
  }
  if (prev.direction === 'asc') return { column, direction: 'desc' };
  return null;
}

const RegionBadge: FC<{ geography: string; size?: 'sm' | 'md' }> = ({
  geography,
  size = 'sm',
}) => {
  const flag = getGeographyFlag(geography);
  const label = getGeographyLabel(geography);
  const md = size === 'md';
  return (
    <span
      className={cn(
        'inline-flex items-center max-w-full rounded-lg border border-[#e5e5e5] font-medium text-neutral-900 tracking-[0.18px]',
        md
          ? 'gap-2 h-8 px-2.5 text-sm bg-white'
          : 'gap-1.5 h-6 px-2 text-xs bg-[#fafafa]'
      )}
    >
      {flag && (
        <img
          src={flag}
          alt=""
          className={cn(
            'shrink-0 object-contain',
            md ? 'size-4' : 'size-[14px]'
          )}
        />
      )}
      <Ellipsis hasTooltip className="truncate">
        {label}
      </Ellipsis>
    </span>
  );
};

const SourceBadge: FC<{
  title: string;
  icon?: string | null;
  geography?: string;
  size?: 'sm' | 'md';
}> = ({ title, icon, geography, size = 'sm' }) => {
  const fallbackFlag = !icon && geography ? getGeographyFlag(geography) : null;
  const imgSrc = icon ?? fallbackFlag;
  const md = size === 'md';

  return (
    <span
      className={cn(
        'inline-flex items-center max-w-full rounded-lg border border-[#e5e5e5] font-medium text-neutral-900 tracking-[0.18px]',
        md
          ? 'gap-2 h-8 px-2.5 text-sm bg-white'
          : 'gap-1.5 h-6 px-2 text-xs bg-[#fafafa]'
      )}
    >
      {imgSrc && (
        <img
          src={imgSrc}
          alt=""
          className={cn(
            'shrink-0 object-contain',
            md ? 'size-[19px]' : 'size-[14px]',
            icon && 'rounded-full border border-[#e5e5e5]'
          )}
        />
      )}
      <Ellipsis hasTooltip className="truncate">
        {title}
      </Ellipsis>
    </span>
  );
};

// ── Reference row inside a group table ──

const ReferenceRow: FC<{
  ref_: AllDocumentsReferenceItem;
  onNavigate: (id: string) => void;
  workspaceUname: string;
  isLast?: boolean;
  columnWidths?: ColumnWidths;
  hideType?: boolean;
  hideRegion?: boolean;
  hideSource?: boolean;
}> = ({
  ref_,
  onNavigate,
  workspaceUname,
  isLast,
  columnWidths,
  hideType,
  hideRegion,
  hideSource,
}) => {
  const cellBorder = 'border-r border-[#e5e5e5]';
  const rowBorder = isLast ? '' : 'border-b border-[#e5e5e5]';

  return (
    <tr
      className={cn(
        'bg-white hover:bg-neutral-50 cursor-pointer transition-colors',
        rowBorder
      )}
      onClick={() => onNavigate(ref_.id)}
    >
      <td
        className={cn('px-2 py-2.5 align-top', cellBorder)}
        style={{ minWidth: 300 }}
      >
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-neutral-900 leading-[1.5] tracking-[0.07px] truncate">
            {ref_.title}
          </span>
          {ref_.description && (
            <p className="text-xs text-neutral-700 tracking-[0.18px] leading-[1.5] line-clamp-2 overflow-hidden">
              {ref_.description}
            </p>
          )}
        </div>
      </td>
      <LastUpdatedCell
        ref_={ref_}
        workspaceUname={workspaceUname}
        columnWidth={columnWidths?.lastUpdated ?? 130}
      />
      {!hideType && (
        <td
          className={cn('px-2 py-3 align-middle overflow-hidden', cellBorder)}
          style={{
            width: columnWidths?.type ?? 160,
            maxWidth: columnWidths?.type ?? 160,
          }}
        >
          {ref_.type && <DocumentTypeBadge type={ref_.type} />}
        </td>
      )}
      {!hideRegion && (
        <td
          className={cn('px-2 py-3 align-middle overflow-hidden', cellBorder)}
          style={{
            width: columnWidths?.region ?? 160,
            maxWidth: columnWidths?.region ?? 160,
          }}
        >
          <RegionBadge geography={ref_.source.geography} />
        </td>
      )}
      {!hideSource && (
        <td
          className={cn('px-2 py-3 align-middle overflow-hidden', cellBorder)}
          style={{
            width: columnWidths?.source ?? 160,
            maxWidth: columnWidths?.source ?? 160,
          }}
        >
          <SourceBadge
            title={ref_.source.title}
            icon={getSourceIconUrl(ref_.source.id, ref_.source.icon)}
            geography={ref_.source.geography}
          />
        </td>
      )}
      <td
        className="px-2 py-3 align-middle text-center"
        style={{ width: columnWidths?.action ?? 72 }}
      >
        {ref_.url && (
          <a
            href={ref_.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            className="inline-flex text-neutral-400 hover:text-neutral-600"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </td>
    </tr>
  );
};

// ── Sortable column header cell ──

const SortableTh: FC<{
  label: string;
  column: SortableColumn;
  sort: TableSortState;
  onCycle: (column: SortableColumn) => void;
  className?: string;
  style?: React.CSSProperties;
  onResize?: (delta: number) => void;
  resizable?: boolean;
}> = ({
  label,
  column,
  sort,
  onCycle,
  className,
  style,
  onResize,
  resizable,
}) => {
  const sortDirection =
    sort !== null && sort.column === column ? sort.direction : false;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!onResize) return;
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const onMove = (ev: MouseEvent) => {
        // Handle is on left edge: dragging right = column shrinks (negative delta)
        onResize(-(ev.clientX - startX));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [onResize]
  );

  return (
    <th
      scope="col"
      className={cn(
        'relative text-left px-2 h-[44px] text-sm font-medium text-neutral-900 leading-[21px] tracking-[0.07px] border-r bg-white',
        className
      )}
      style={style}
    >
      <ControlledSortColumnHeader
        label={<span className="truncate">{label}</span>}
        sortDirection={sortDirection}
        onSortClick={() => onCycle(column)}
      />
      {resizable && (
        <div
          onMouseDown={handleMouseDown}
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400/40 active:bg-blue-400/60"
        />
      )}
    </th>
  );
};

// ── Global sticky column header ──

const GlobalDocumentsTableHeader: FC<{
  sort: TableSortState;
  onCycle: (column: SortableColumn) => void;
  indented?: boolean;
  columnWidths: ColumnWidths;
  onColumnResize: (key: keyof ColumnWidths, width: number) => void;
  hideType?: boolean;
  hideRegion?: boolean;
  hideSource?: boolean;
}> = ({
  sort,
  onCycle,
  indented = true,
  columnWidths,
  onColumnResize,
  hideType,
  hideRegion,
  hideSource,
}) => {
  // Track the width at drag start so delta is relative
  const startWidthRef = useRef<number>(0);

  const makeResizeHandler = useCallback(
    (key: keyof ColumnWidths) => (delta: number) => {
      if (delta === 0) return;
      // On first move, capture starting width
      if (startWidthRef.current === 0)
        startWidthRef.current = columnWidths[key];
      const newWidth = startWidthRef.current + delta;
      onColumnResize(key, newWidth);
    },
    [columnWidths, onColumnResize]
  );

  const nameMinWidth = 400;

  // Reset start ref on mouseup (handled inside SortableTh)
  useEffect(() => {
    const reset = () => {
      startWidthRef.current = 0;
    };
    document.addEventListener('mouseup', reset);
    return () => document.removeEventListener('mouseup', reset);
  }, []);

  return (
    <div
      className={cn(
        'sticky top-0 z-10 bg-white border-b border-[#e5e5e5]',
        indented && 'px-6'
      )}
    >
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr>
            <SortableTh
              label="Reference"
              column="name"
              sort={sort}
              onCycle={onCycle}
              className={indented ? 'pl-[34px]' : 'pl-6'}
              style={{ minWidth: nameMinWidth }}
            />
            <SortableTh
              label="Last updated"
              column="lastUpdated"
              sort={sort}
              onCycle={onCycle}
              className="overflow-hidden"
              style={{ width: columnWidths.lastUpdated }}
              resizable
              onResize={makeResizeHandler('lastUpdated')}
            />
            {!hideType && (
              <SortableTh
                label="Reference type"
                column="type"
                sort={sort}
                onCycle={onCycle}
                className="overflow-hidden"
                style={{ width: columnWidths.type }}
                resizable
                onResize={makeResizeHandler('type')}
              />
            )}
            {!hideRegion && (
              <SortableTh
                label="Regulatory region"
                column="region"
                sort={sort}
                onCycle={onCycle}
                className="overflow-hidden"
                style={{ width: columnWidths.region }}
                resizable
                onResize={makeResizeHandler('region')}
              />
            )}
            {!hideSource && (
              <SortableTh
                label="Source"
                column="source"
                sort={sort}
                onCycle={onCycle}
                className="overflow-hidden"
                style={{ width: columnWidths.source }}
                resizable
                onResize={makeResizeHandler('source')}
              />
            )}
            <th
              scope="col"
              className={cn('bg-white', !indented && 'pr-6')}
              style={{ width: columnWidths.action }}
            />
          </tr>
        </thead>
      </table>
    </div>
  );
};

// ── Source group (collapsible) ──

const groupTableContentClassName = cn(
  'overflow-hidden ml-[34px]',
  'data-[state=closed]:animate-rw-group-slide-up data-[state=open]:animate-rw-group-slide-down'
);

const SourceGroupSection: FC<{
  group: AllDocumentsSourceGroup;
  onNavigate: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnWidths: ColumnWidths;
  workspaceUname: string;
}> = ({
  group,
  onNavigate,
  open,
  onOpenChange,
  columnWidths,
  workspaceUname,
}) => {
  const icon = getSourceIconUrl(group.source.id, group.source.icon);
  const refs = group.references;

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      className="mb-5"
    >
      <CollapsiblePrimitive.Trigger asChild>
        <button
          type="button"
          className="flex items-center w-full gap-1.5 pl-1 pr-6 pt-5 pb-2 text-left outline-none"
        >
          <ChevronDown
            className={cn(
              'size-6 text-neutral-700 transition-transform duration-200 ease-in-out shrink-0',
              !open && '-rotate-90'
            )}
          />
          <Folder
            className="size-5 shrink-0 text-neutral-900"
            strokeWidth={2}
          />
          <SourceBadge
            title={group.source.title}
            icon={icon}
            geography={group.source.geography}
            size="md"
          />
          <span className="text-xs text-neutral-700 tracking-[0.18px] ml-auto shrink-0">
            {group.documentCount} reference
            {group.documentCount !== 1 ? 's' : ''}
          </span>
        </button>
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content className={groupTableContentClassName}>
        <div className="rounded-lg border border-[#e5e5e5] overflow-hidden bg-white">
          <table className="w-full text-sm table-fixed">
            <tbody>
              {refs.map((ref_, i) => (
                <ReferenceRow
                  key={ref_.id}
                  ref_={ref_}
                  onNavigate={onNavigate}
                  workspaceUname={workspaceUname}
                  isLast={i === refs.length - 1}
                  columnWidths={columnWidths}
                  hideSource
                />
              ))}
            </tbody>
          </table>
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
};

// ── Country group (collapsible) ──

const CountryGroupSection: FC<{
  group: AllDocumentsCountryGroup;
  onNavigate: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnWidths: ColumnWidths;
  workspaceUname: string;
}> = ({
  group,
  onNavigate,
  open,
  onOpenChange,
  columnWidths,
  workspaceUname,
}) => {
  const refs = group.references;

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      className="mb-5"
    >
      <CollapsiblePrimitive.Trigger asChild>
        <button
          type="button"
          className="flex items-center w-full gap-1.5 pl-1 pr-6 pt-5 pb-2 text-left outline-none"
        >
          <ChevronDown
            className={cn(
              'size-6 text-neutral-700 transition-transform duration-200 ease-in-out shrink-0',
              !open && '-rotate-90'
            )}
          />
          <Folder
            className="size-5 shrink-0 text-neutral-900"
            strokeWidth={2}
          />
          <RegionBadge geography={group.geography} size="md" />
          <span className="text-xs text-neutral-700 tracking-[0.18px] ml-auto shrink-0">
            {group.documentCount} reference
            {group.documentCount !== 1 ? 's' : ''}
          </span>
        </button>
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content className={groupTableContentClassName}>
        <div className="rounded-lg border border-[#e5e5e5] overflow-hidden bg-white">
          <table className="w-full text-sm table-fixed">
            <tbody>
              {refs.map((ref_, i) => (
                <ReferenceRow
                  key={ref_.id}
                  ref_={ref_}
                  onNavigate={onNavigate}
                  workspaceUname={workspaceUname}
                  isLast={i === refs.length - 1}
                  columnWidths={columnWidths}
                  hideRegion
                />
              ))}
            </tbody>
          </table>
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
};

// ── Type group (collapsible) ──

type TypeGroup = {
  type: string;
  references: AllDocumentsReferenceItem[];
  documentCount: number;
};

const TypeGroupBadge: FC<{ type: string }> = ({ type }) => {
  const config = TYPE_BADGE_CONFIG[type];
  const label = SOURCE_TYPE_LABELS[type] ?? type;
  const TypeIcon = config?.icon;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 h-8 px-2.5 rounded-lg text-base font-medium text-neutral-900 tracking-[0.24px]',
        config?.bgClass ?? 'bg-neutral-100'
      )}
    >
      {TypeIcon && <TypeIcon className="size-[19px]" />}
      {label}
    </span>
  );
};

const TypeGroupSection: FC<{
  group: TypeGroup;
  onNavigate: (id: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnWidths: ColumnWidths;
  workspaceUname: string;
}> = ({
  group,
  onNavigate,
  open,
  onOpenChange,
  columnWidths,
  workspaceUname,
}) => {
  const refs = group.references;

  return (
    <CollapsiblePrimitive.Root
      open={open}
      onOpenChange={onOpenChange}
      className="mb-5"
    >
      <CollapsiblePrimitive.Trigger asChild>
        <button
          type="button"
          className="flex items-center w-full gap-1.5 pl-1 pr-6 pt-5 pb-2 text-left outline-none"
        >
          <ChevronDown
            className={cn(
              'size-6 text-neutral-700 transition-transform duration-200 ease-in-out shrink-0',
              !open && '-rotate-90'
            )}
          />
          <Folder
            className="size-5 shrink-0 text-neutral-900"
            strokeWidth={2}
          />
          <TypeGroupBadge type={group.type} />
          <span className="text-xs text-neutral-700 tracking-[0.18px] ml-auto shrink-0">
            {group.documentCount} reference
            {group.documentCount !== 1 ? 's' : ''}
          </span>
        </button>
      </CollapsiblePrimitive.Trigger>

      <CollapsiblePrimitive.Content className={groupTableContentClassName}>
        <div className="rounded-lg border border-[#e5e5e5] overflow-hidden bg-white">
          <table className="w-full text-sm table-fixed">
            <tbody>
              {refs.map((ref_, i) => (
                <ReferenceRow
                  key={ref_.id}
                  ref_={ref_}
                  onNavigate={onNavigate}
                  workspaceUname={workspaceUname}
                  isLast={i === refs.length - 1}
                  columnWidths={columnWidths}
                  hideType
                />
              ))}
            </tbody>
          </table>
        </div>
      </CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
};

// ── Group by picker ──

const GROUP_BY_OPTIONS: { value: GroupBy; label: string }[] = [
  { value: 'type', label: 'Type' },
  { value: 'country', label: 'Regulatory region' },
  { value: 'source', label: 'Source' },
  { value: 'none', label: 'No grouping' },
];

const GROUP_BY_VALUES: readonly GroupBy[] = [
  'source',
  'country',
  'type',
  'none',
];

const GroupByPicker: FC<{
  value: GroupBy;
  onChange: (value: GroupBy) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-sm font-medium text-neutral-900 tracking-[0.07px]">
        Group by:
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center h-9 w-[200px] pl-3 pr-2 rounded-lg border border-neutral-300 bg-white shadow-sm text-sm text-neutral-900 tracking-[0.07px]">
            <span className="flex-1 text-left">
              {GROUP_BY_OPTIONS.find(o => o.value === value)?.label}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-neutral-500" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto min-w-[200px] p-0" align="start">
          <div className="py-1">
            {GROUP_BY_OPTIONS.map(option => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent"
                >
                  <span>{option.label}</span>
                  {isSelected ? (
                    <Check className="size-4 shrink-0 text-primary" />
                  ) : (
                    <span className="size-4 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={value === 'none'}
        onClick={() => onChange('none')}
        className="shrink-0 border-0 text-[12px] font-medium leading-[160%] tracking-[0.015em] shadow-none"
      >
        Clear
      </Button>
    </div>
  );
};

// ── Main page ──

export const AllDocumentsPage: FC = () => {
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const sessionRestoredOpenKeysRef = useRef<string[] | null>(null);

  useRegulatoryWatchSearchSession('all-documents');

  useLayoutEffect(() => {
    if (!workspaceUname) {
      sessionRestoredOpenKeysRef.current = null;
      return;
    }
    const keys = loadRwPageSession(
      workspaceUname,
      'all-documents'
    )?.openGroupKeys;
    sessionRestoredOpenKeysRef.current =
      keys && keys.length > 0 ? [...keys] : null;
  }, [workspaceUname]);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const basePath = `/workspace/${workspaceUname}/regulatory-watch`;

  const [searchInput, setSearchInput] = useUrlParam('search');
  const debouncedSearch = useDebounce(searchInput, 300);
  const [groupBy, setGroupBy] = useUrlParamEnum<GroupBy>(
    'groupBy',
    GROUP_BY_VALUES,
    'source'
  );
  const [selectedDocTypes, setSelectedDocTypes] =
    useUrlParamCsv('documentType');
  const [selectedCountries, setSelectedCountries] = useUrlParamCsv('geography');
  const [selectedSources, setSelectedSources] = useUrlParamCsv('source');
  const [lastUpdatedPreset] = useUrlParamEnum<LastUpdatedPreset>(
    'lastUpdatedPreset',
    LAST_UPDATED_PRESET_VALUES,
    'none'
  );
  const [lastUpdatedFrom, setLastUpdatedFrom] = useUrlParam('lastUpdatedFrom');
  const [lastUpdatedTo, setLastUpdatedTo] = useUrlParam('lastUpdatedTo');

  const [optionalFilterOrder, setOptionalFilterOrder] = useState<
    Array<'geography' | 'source'>
  >(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const out: Array<'geography' | 'source'> = [];
      if (p.get('geography')?.trim()) out.push('geography');
      if (p.get('source')?.trim()) out.push('source');
      return out;
    } catch {
      return [];
    }
  });

  const handleCountriesChange = useCallback(
    (next: string[]) => {
      setSelectedCountries(next);
      if (next.length === 0) {
        setOptionalFilterOrder(prev => prev.filter(k => k !== 'geography'));
      }
    },
    [setSelectedCountries]
  );

  const handleSourcesChange = useCallback(
    (next: string[]) => {
      setSelectedSources(next);
      if (next.length === 0) {
        setOptionalFilterOrder(prev => prev.filter(k => k !== 'source'));
      }
    },
    [setSelectedSources]
  );

  const addOptionalMyReferencesFilter = useCallback(
    (key: 'geography' | 'source') => {
      setOptionalFilterOrder(prev =>
        prev.includes(key) ? prev : [...prev, key]
      );
    },
    []
  );

  useEffect(() => {
    if (selectedCountries.length > 0) {
      setOptionalFilterOrder(prev =>
        prev.includes('geography') ? prev : [...prev, 'geography']
      );
    }
  }, [selectedCountries.length]);

  useEffect(() => {
    if (selectedSources.length > 0) {
      setOptionalFilterOrder(prev =>
        prev.includes('source') ? prev : [...prev, 'source']
      );
    }
  }, [selectedSources.length]);

  const effectiveLastUpdatedPreset = useMemo((): LastUpdatedPreset => {
    if (lastUpdatedPreset !== 'none') return lastUpdatedPreset;
    if (lastUpdatedFrom && lastUpdatedTo) return 'custom';
    return 'none';
  }, [lastUpdatedPreset, lastUpdatedFrom, lastUpdatedTo]);

  const hasActiveMyReferencesFilters = useMemo(() => {
    if (selectedDocTypes.length > 0) return true;
    if (selectedCountries.length > 0) return true;
    if (selectedSources.length > 0) return true;
    if (effectiveLastUpdatedPreset !== 'none') {
      if (effectiveLastUpdatedPreset === 'custom') {
        return Boolean(lastUpdatedFrom && lastUpdatedTo);
      }
      return true;
    }
    return false;
  }, [
    selectedDocTypes.length,
    selectedCountries.length,
    selectedSources.length,
    effectiveLastUpdatedPreset,
    lastUpdatedFrom,
    lastUpdatedTo,
  ]);

  const clearAllMyReferencesFilters = useCallback(() => {
    setSelectedDocTypes([]);
    setSelectedCountries([]);
    setSelectedSources([]);
    setOptionalFilterOrder([]);
    setSearchParams(
      prev => {
        const p = new URLSearchParams(prev);
        p.delete('lastUpdatedPreset');
        p.delete('lastUpdatedFrom');
        p.delete('lastUpdatedTo');
        return p;
      },
      { replace: true }
    );
  }, [
    setSearchParams,
    setSelectedDocTypes,
    setSelectedCountries,
    setSelectedSources,
  ]);

  const { geographyCodes, sourceOptionsForFilter, expandSourceFilter } =
    useSourcesService();

  // Backend only supports 'source' | 'country'. For 'type' and 'none' we
  // fetch by 'source' and re-group on the frontend.
  const backendGroupBy = groupBy === 'country' ? 'country' : 'source';

  // Column sort state
  const [tableSort, setTableSort] = useState<TableSortState>(() =>
    parsePersistedTableSort(
      workspaceUname
        ? (loadRwPageSession(workspaceUname, 'all-documents')?.tableSort ??
            null)
        : null
    )
  );

  const filters: ListAllDocumentsQuery = useMemo(() => {
    const f: ListAllDocumentsQuery = { groupBy: backendGroupBy };
    if (debouncedSearch) f.search = debouncedSearch;
    if (selectedDocTypes.length > 0)
      f.documentType = selectedDocTypes.join(',');
    if (selectedCountries.length > 0) f.geography = selectedCountries.join(',');
    if (selectedSources.length > 0) {
      f.source = expandSourceFilter(selectedSources).join(',');
    }
    if (effectiveLastUpdatedPreset !== 'none') {
      f.lastUpdatedPreset = effectiveLastUpdatedPreset;
    }
    if (
      effectiveLastUpdatedPreset === 'custom' &&
      lastUpdatedFrom &&
      lastUpdatedTo
    ) {
      f.lastUpdatedFrom = lastUpdatedFrom;
      f.lastUpdatedTo = lastUpdatedTo;
    }
    if (tableSort) {
      f.sortBy = tableSort.column;
      f.sortDirection = tableSort.direction;
    }
    return f;
  }, [
    backendGroupBy,
    debouncedSearch,
    selectedDocTypes,
    selectedCountries,
    selectedSources,
    effectiveLastUpdatedPreset,
    lastUpdatedFrom,
    lastUpdatedTo,
    expandSourceFilter,
    tableSort,
  ]);

  const [columnWidths, setColumnWidths] = useState<ColumnWidths>(
    DEFAULT_COLUMN_WIDTHS
  );
  const [refPagination, setRefPagination] = useState({
    pageIndex: 0,
    pageSize: REGULATORY_WATCH_TABLE_PAGE_SIZE,
  });
  const contentRef = useRef<HTMLDivElement>(null);

  const { allDocuments, isLoading, isFetching } =
    useAllDocumentsService(filters);
  usePersistToRwSession('all-documents', tableSort, sort => ({
    tableSort: sort ? { column: sort.column, direction: sort.direction } : null,
  }));

  usePersistToRwSession('all-documents', columnWidths, w => ({
    columnWidths: w,
  }));

  const handleCycleSort = useCallback((column: SortableColumn) => {
    setTableSort(prev => cycleTableSort(prev, column));
  }, []);

  const NAME_MIN_WIDTH = 400;
  const COL_MIN_WIDTH = 120;

  const handleColumnResize = useCallback(
    (key: keyof ColumnWidths, width: number) => {
      setColumnWidths(prev => {
        const clamped = Math.max(COL_MIN_WIDTH, width);
        const next = { ...prev, [key]: clamped };
        // Ensure Name column keeps at least 300px
        const containerWidth = contentRef.current?.clientWidth ?? 0;
        if (containerWidth > 0) {
          const totalFixed =
            next.lastUpdated +
            next.type +
            next.region +
            next.source +
            next.action;
          const nameWidth = containerWidth - totalFixed;
          if (nameWidth < NAME_MIN_WIDTH) {
            // Reduce the resized column to respect Name min
            const maxAllowed = clamped - (NAME_MIN_WIDTH - nameWidth);
            next[key] = Math.max(COL_MIN_WIDTH, maxAllowed);
          }
        }
        return next;
      });
    },
    []
  );

  const sourceGroups = allDocuments?.sourceGroups ?? null;
  const countryGroups = allDocuments?.countryGroups ?? null;

  const hasSourceGroupRows = !!sourceGroups?.length;
  const hasCountryGroupRows = !!countryGroups?.length;

  const usesSourceBackedLayout =
    groupBy === 'source' || groupBy === 'type' || groupBy === 'none';

  const awaitingSourceBackedLayout =
    usesSourceBackedLayout &&
    isFetching &&
    hasCountryGroupRows &&
    !hasSourceGroupRows;

  const awaitingCountryOnlyLayout =
    groupBy === 'country' &&
    isFetching &&
    hasSourceGroupRows &&
    !hasCountryGroupRows;

  const showListSkeleton =
    isLoading || awaitingSourceBackedLayout || awaitingCountryOnlyLayout;

  useRegulatoryWatchScrollSession(
    'all-documents',
    contentRef,
    !showListSkeleton
  );

  // Flatten all refs from source groups (for 'type' and 'none' modes)
  // Refs come pre-sorted from the backend.
  const allRefs = useMemo((): AllDocumentsReferenceItem[] => {
    if (!sourceGroups) return [];
    return sourceGroups.flatMap(g => g.references);
  }, [sourceGroups]);

  // Group by document type (frontend-side, refs already sorted by backend)
  const typeGroups = useMemo((): TypeGroup[] => {
    if (groupBy !== 'type') return [];
    const map = new Map<string, AllDocumentsReferenceItem[]>();
    for (const ref of allRefs) {
      const t = ref.type ?? 'unknown';
      const list = map.get(t) ?? [];
      list.push(ref);
      map.set(t, list);
    }
    const order = ['regulation', 'guidance', 'standard', 'news', 'unknown'];
    return order
      .filter(t => map.has(t))
      .map(t => ({
        type: t,
        references: map.get(t)!,
        documentCount: map.get(t)!.length,
      }));
  }, [groupBy, allRefs]);

  // Flat refs for 'none' mode (already sorted by backend)
  const flatRefs = useMemo((): AllDocumentsReferenceItem[] => {
    if (groupBy !== 'none') return [];
    return allRefs;
  }, [groupBy, allRefs]);

  useEffect(() => {
    setRefPagination(p => ({ ...p, pageIndex: 0 }));
  }, [groupBy, allRefs.length]);

  const flatRefsPageCount = useMemo(
    () => Math.max(1, Math.ceil(flatRefs.length / refPagination.pageSize)),
    [flatRefs.length, refPagination.pageSize]
  );

  const safeRefPageIndex = Math.min(
    refPagination.pageIndex,
    flatRefsPageCount - 1
  );

  useEffect(() => {
    if (safeRefPageIndex !== refPagination.pageIndex) {
      setRefPagination(p => ({ ...p, pageIndex: safeRefPageIndex }));
    }
  }, [safeRefPageIndex, refPagination.pageIndex]);

  const paginatedFlatRefs = useMemo((): AllDocumentsReferenceItem[] => {
    const start = safeRefPageIndex * refPagination.pageSize;
    return flatRefs.slice(start, start + refPagination.pageSize);
  }, [flatRefs, safeRefPageIndex, refPagination.pageSize]);

  const totalDocuments = useMemo(() => {
    if (groupBy === 'none' || groupBy === 'type') return allRefs.length;
    if (!allDocuments) return 0;
    if (groupBy === 'source') {
      if (!allDocuments.sourceGroups?.length) return 0;
      return allDocuments.sourceGroups.reduce((s, g) => s + g.documentCount, 0);
    }
    if (groupBy === 'country') {
      if (!allDocuments.countryGroups?.length) return 0;
      return allDocuments.countryGroups.reduce(
        (s, g) => s + g.documentCount,
        0
      );
    }
    return 0;
  }, [groupBy, allRefs, allDocuments]);

  const currentGroupIds = useMemo((): string[] => {
    if (groupBy === 'source' && sourceGroups) {
      return sourceGroups.map(g => g.source.id);
    }
    if (groupBy === 'country' && countryGroups) {
      return countryGroups.map(g => g.geography);
    }
    if (groupBy === 'type') {
      return typeGroups.map(g => g.type);
    }
    return [];
  }, [groupBy, sourceGroups, countryGroups, typeGroups]);

  const [openGroupKeys, setOpenGroupKeys] = useState<Set<string>>(
    () => new Set()
  );

  const prevGroupIdsRef = useRef<string[] | undefined>(undefined);
  const prevGroupByRef = useRef<GroupBy | undefined>(undefined);

  useEffect(() => {
    const restoreOpenKeysFromSession = () => {
      if (!sessionRestoredOpenKeysRef.current) return;
      const allowed = new Set(currentGroupIds);
      setOpenGroupKeys(
        new Set(sessionRestoredOpenKeysRef.current.filter(k => allowed.has(k)))
      );
      sessionRestoredOpenKeysRef.current = null;
    };

    const prevIds = prevGroupIdsRef.current;
    const prevGroupBy = prevGroupByRef.current;

    if (prevIds === undefined) {
      prevGroupByRef.current = groupBy;
      prevGroupIdsRef.current = currentGroupIds;

      if (currentGroupIds.length === 0) {
        return;
      }

      // Cached documents (e.g. React Query): groups exist on the first effect run.
      // Apply saved expansion here — otherwise we skip the "loading → loaded" branch below.
      restoreOpenKeysFromSession();
      return;
    }

    // Initial fetch for the same group mode: keep collapsed unless session restores keys.
    if (
      groupBy === prevGroupBy &&
      prevIds.length === 0 &&
      currentGroupIds.length > 0
    ) {
      prevGroupIdsRef.current = currentGroupIds;
      prevGroupByRef.current = groupBy;
      restoreOpenKeysFromSession();
      return;
    }

    setOpenGroupKeys(new Set(currentGroupIds));
    prevGroupIdsRef.current = currentGroupIds;
    prevGroupByRef.current = groupBy;
  }, [currentGroupIds, groupBy]);

  const handleGroupToggle = useCallback((key: string, open: boolean) => {
    setOpenGroupKeys(prev => {
      const next = new Set(prev);
      open ? next.add(key) : next.delete(key);
      return next;
    });
  }, []);

  usePersistToRwSession('all-documents', openGroupKeys, keys => ({
    openGroupKeys: [...keys],
  }));

  const allExpanded =
    currentGroupIds.length > 0 &&
    currentGroupIds.every(id => openGroupKeys.has(id));

  const handleExpandAll = useCallback(() => {
    setOpenGroupKeys(new Set(currentGroupIds));
  }, [currentGroupIds]);

  const handleCollapseAll = useCallback(() => {
    setOpenGroupKeys(new Set());
  }, []);

  const handleNavigate = useCallback(
    (referenceId: string) => {
      const qs = searchParams.toString();
      navigate(`${basePath}/references/${referenceId}${qs ? `?${qs}` : ''}`, {
        state: {
          backPath: `${basePath}/all-documents`,
          backLabel: 'My references',
        },
      });
    },
    [navigate, basePath, searchParams]
  );

  const hideType = groupBy === 'type';
  const hideRegion = groupBy === 'country';
  const hideSource = groupBy === 'source';

  return (
    <LastUpdatedPreviewProvider>
      <div className="flex flex-col h-full min-h-0">
        {/* Controls */}
        <div className="shrink-0 bg-white border-b border-neutral-200 px-6 py-4 flex flex-col gap-4">
          {/* Row 1: Group by + Search */}
          <div className="flex items-center gap-2.5">
            <GroupByPicker value={groupBy} onChange={setGroupBy} />
            <div className="ml-auto relative w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                placeholder="Search"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="pl-9 h-9 text-sm"
              />
            </div>
          </div>

          {/* Row 2: Filters + Expand/Collapse */}
          <div className="flex items-center gap-4 flex-wrap min-w-0">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <FilterPopoverTrigger
                label="Reference type"
                isActive={selectedDocTypes.length > 0}
                operator={
                  selectedDocTypes.length > 0
                    ? selectedDocTypes.length === 1
                      ? 'is'
                      : 'is any of'
                    : undefined
                }
                activeContent={
                  selectedDocTypes.length > 0
                    ? renderReferenceTypeFilterSummary(selectedDocTypes)
                    : undefined
                }
                onClear={
                  selectedDocTypes.length > 0
                    ? () => setSelectedDocTypes([])
                    : undefined
                }
                {...MY_REFERENCES_FILTER_TRIGGER}
              >
                <FilterOptionList
                  options={SOURCE_TYPE_OPTIONS}
                  selected={selectedDocTypes}
                  onChange={setSelectedDocTypes}
                />
              </FilterPopoverTrigger>

              <LastUpdatedFilter
                effectivePreset={effectiveLastUpdatedPreset}
                lastUpdatedFrom={lastUpdatedFrom}
                lastUpdatedTo={lastUpdatedTo}
                setLastUpdatedPreset={v => {
                  setSearchParams(
                    prev => {
                      const p = new URLSearchParams(prev);
                      if (v === 'none') {
                        p.delete('lastUpdatedPreset');
                        p.delete('lastUpdatedFrom');
                        p.delete('lastUpdatedTo');
                      } else {
                        p.set('lastUpdatedPreset', v);
                        if (v !== 'custom') {
                          p.delete('lastUpdatedFrom');
                          p.delete('lastUpdatedTo');
                        }
                      }
                      return p;
                    },
                    { replace: true }
                  );
                }}
                setLastUpdatedFrom={setLastUpdatedFrom}
                setLastUpdatedTo={setLastUpdatedTo}
                clearLastUpdated={() => {
                  setSearchParams(
                    prev => {
                      const p = new URLSearchParams(prev);
                      p.delete('lastUpdatedPreset');
                      p.delete('lastUpdatedFrom');
                      p.delete('lastUpdatedTo');
                      return p;
                    },
                    { replace: true }
                  );
                }}
                onApplyCustomRange={(from, to) => {
                  setSearchParams(
                    prev => {
                      const p = new URLSearchParams(prev);
                      p.set('lastUpdatedPreset', 'custom');
                      p.set('lastUpdatedFrom', from);
                      p.set('lastUpdatedTo', to);
                      return p;
                    },
                    { replace: true }
                  );
                }}
                filterPopoverProps={MY_REFERENCES_FILTER_TRIGGER}
              />

              {optionalFilterOrder.map(key => {
                if (key === 'geography') {
                  return (
                    <CountryFilter
                      key="geography"
                      label="Regulatory region"
                      countries={geographyCodes}
                      selected={selectedCountries}
                      onChange={handleCountriesChange}
                      filterPopoverProps={MY_REFERENCES_FILTER_TRIGGER}
                      activePreview={
                        selectedCountries.length > 0
                          ? renderGeographyFilterSummary(selectedCountries)
                          : undefined
                      }
                    />
                  );
                }
                if (key === 'source') {
                  return (
                    <FilterPopoverTrigger
                      key="source"
                      label="Source"
                      isActive={selectedSources.length > 0}
                      operator={
                        selectedSources.length > 0
                          ? selectedSources.length === 1
                            ? 'is'
                            : 'is any of'
                          : undefined
                      }
                      activeContent={
                        selectedSources.length > 0
                          ? renderSourceFilterSummary(
                              selectedSources,
                              sourceOptionsForFilter
                            )
                          : undefined
                      }
                      onClear={
                        selectedSources.length > 0
                          ? () => handleSourcesChange([])
                          : undefined
                      }
                      {...MY_REFERENCES_FILTER_TRIGGER}
                    >
                      <FilterOptionList
                        options={sourceOptionsForFilter}
                        selected={selectedSources}
                        onChange={handleSourcesChange}
                      />
                    </FilterPopoverTrigger>
                  );
                }
                return null;
              })}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 rounded-full border-dashed px-2.5 text-xs font-medium gap-1"
                  >
                    <Plus className="size-3.5 shrink-0" />
                    Add filter
                    <ChevronDown className="size-3 shrink-0 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[240px]">
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Default filters
                  </DropdownMenuLabel>
                  <DropdownMenuItem disabled className="opacity-100">
                    Reference type
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="opacity-100">
                    Last updated
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Optional filters
                  </DropdownMenuLabel>
                  {!optionalFilterOrder.includes('geography') && (
                    <DropdownMenuItem
                      onSelect={() =>
                        addOptionalMyReferencesFilter('geography')
                      }
                    >
                      Regulatory region
                    </DropdownMenuItem>
                  )}
                  {!optionalFilterOrder.includes('source') && (
                    <DropdownMenuItem
                      onSelect={() => addOptionalMyReferencesFilter('source')}
                    >
                      Source
                    </DropdownMenuItem>
                  )}
                  {optionalFilterOrder.includes('geography') &&
                    optionalFilterOrder.includes('source') && (
                      <DropdownMenuItem disabled className="opacity-60">
                        All optional filters are active
                      </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {hasActiveMyReferencesFilters && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 shrink-0 px-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={clearAllMyReferencesFilters}
              >
                Clear
              </Button>
            )}

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleExpandAll}
                disabled={groupBy === 'none' || allExpanded}
                className="flex items-center gap-1.5 px-2 py-[3px] rounded-[4px] border border-[#e5e5e5] text-xs font-medium text-neutral-900 tracking-[0.18px] shadow-[0_1px_1px_rgba(0,0,0,0.05)] disabled:opacity-50"
              >
                <ChevronsUpDown className="size-[13px]" />
                Expand all
              </button>
              <button
                type="button"
                onClick={handleCollapseAll}
                disabled={groupBy === 'none' || openGroupKeys.size === 0}
                className="flex items-center gap-1.5 px-2 py-[3px] rounded-[4px] border border-[#e5e5e5] text-xs font-medium text-neutral-900 tracking-[0.18px] shadow-[0_1px_1px_rgba(0,0,0,0.05)] disabled:opacity-50"
              >
                <ChevronsDownUp className="size-[13px]" />
                Collapse all
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          ref={contentRef}
          className={cn(
            'flex-1 overflow-y-auto',
            groupBy !== 'none' && 'bg-gray-50'
          )}
        >
          {showListSkeleton ? (
            <div className="flex flex-col gap-6 px-6 pt-6">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-48 w-full rounded-lg" />
              ))}
            </div>
          ) : totalDocuments === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-neutral-500">
              <FileText className="h-12 w-12 text-neutral-300" />
              <span className="text-sm">No references found</span>
            </div>
          ) : (
            <>
              {/* Sticky column header */}
              <GlobalDocumentsTableHeader
                sort={tableSort}
                onCycle={handleCycleSort}
                indented={groupBy !== 'none'}
                columnWidths={columnWidths}
                onColumnResize={handleColumnResize}
                hideType={hideType}
                hideRegion={hideRegion}
                hideSource={hideSource}
              />

              <div className={groupBy === 'none' ? '' : 'px-6 pb-6'}>
                {groupBy === 'source' &&
                  sourceGroups?.map(group => (
                    <SourceGroupSection
                      key={group.source.id}
                      group={group}
                      onNavigate={handleNavigate}
                      open={openGroupKeys.has(group.source.id)}
                      onOpenChange={open =>
                        handleGroupToggle(group.source.id, open)
                      }
                      columnWidths={columnWidths}
                      workspaceUname={workspaceUname!}
                    />
                  ))}
                {groupBy === 'country' &&
                  countryGroups?.map(group => (
                    <CountryGroupSection
                      key={group.geography}
                      group={group}
                      onNavigate={handleNavigate}
                      open={openGroupKeys.has(group.geography)}
                      onOpenChange={open =>
                        handleGroupToggle(group.geography, open)
                      }
                      columnWidths={columnWidths}
                      workspaceUname={workspaceUname!}
                    />
                  ))}
                {groupBy === 'type' &&
                  typeGroups.map(group => (
                    <TypeGroupSection
                      key={group.type}
                      group={group}
                      onNavigate={handleNavigate}
                      open={openGroupKeys.has(group.type)}
                      onOpenChange={open => handleGroupToggle(group.type, open)}
                      columnWidths={columnWidths}
                      workspaceUname={workspaceUname!}
                    />
                  ))}
                {groupBy === 'none' && (
                  <>
                    <table className="w-full text-sm table-fixed bg-white border-collapse">
                      <tbody>
                        {paginatedFlatRefs.map(ref_ => (
                          <tr
                            key={ref_.id}
                            className="bg-white hover:bg-neutral-50 cursor-pointer transition-colors border-b border-[#e5e5e5]"
                            onClick={() => handleNavigate(ref_.id)}
                          >
                            <td
                              className="px-2 py-2.5 pl-6 align-top border-r border-[#e5e5e5]"
                              style={{ minWidth: 300 }}
                            >
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-neutral-900 leading-[1.5] tracking-[0.07px] truncate">
                                  {ref_.title}
                                </span>
                                {ref_.description && (
                                  <p className="text-xs text-neutral-700 tracking-[0.18px] leading-[1.5] line-clamp-2 overflow-hidden">
                                    {ref_.description}
                                  </p>
                                )}
                              </div>
                            </td>
                            <LastUpdatedCell
                              ref_={ref_}
                              workspaceUname={workspaceUname!}
                              columnWidth={columnWidths.lastUpdated}
                            />
                            <td
                              className="px-2 py-3 align-middle overflow-hidden border-r border-[#e5e5e5]"
                              style={{
                                width: columnWidths.type,
                                maxWidth: columnWidths.type,
                              }}
                            >
                              {ref_.type && (
                                <DocumentTypeBadge type={ref_.type} />
                              )}
                            </td>
                            <td
                              className="px-2 py-3 align-middle overflow-hidden border-r border-[#e5e5e5]"
                              style={{
                                width: columnWidths.region,
                                maxWidth: columnWidths.region,
                              }}
                            >
                              <RegionBadge geography={ref_.source.geography} />
                            </td>
                            <td
                              className="px-2 py-3 align-middle overflow-hidden border-r border-[#e5e5e5]"
                              style={{
                                width: columnWidths.source,
                                maxWidth: columnWidths.source,
                              }}
                            >
                              <SourceBadge
                                title={ref_.source.title}
                                icon={getSourceIconUrl(
                                  ref_.source.id,
                                  ref_.source.icon
                                )}
                                geography={ref_.source.geography}
                              />
                            </td>
                            <td
                              className="px-2 py-3 pr-6 align-middle text-center"
                              style={{ width: columnWidths.action }}
                            >
                              {ref_.url && (
                                <a
                                  href={ref_.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  className="inline-flex text-neutral-400 hover:text-neutral-600"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {!isLoading &&
          groupBy === 'none' &&
          totalDocuments > 0 &&
          flatRefs.length > 0 && (
            <TablePaginationStickyFooter
              totalCount={flatRefs.length}
              pageIndex={refPagination.pageIndex}
              pageSize={refPagination.pageSize}
              onPageIndexChange={pageIndex =>
                setRefPagination(p => ({ ...p, pageIndex }))
              }
              entityLabel="references"
            />
          )}
      </div>
    </LastUpdatedPreviewProvider>
  );
};
